import { type ReactNode, useEffect, useState } from 'react';
import { uniqueId } from 'lodash-es';
import moment from 'moment';
import { Date } from 'components/date';
import { DatePicker } from 'components/date-picker/date-picker';
import { EditPencilButton } from 'components/edit-pencil-button/edit-pencil-button';
import styles from './editable-date-cell.module.scss';

/** Empty-cell indicator (em dash). */
const EMPTY = '—';

export interface EditableDateCellProps {
  /** The displayed date value (`yyyy-MM-dd`), or `undefined` when empty. */
  value: string | undefined;
  /**
   * Optional field label. When provided it labels the date picker and the edit
   * tooltip/aria ("Edit {label}"); when omitted, the picker shows no visible
   * label and the edit affordance falls back to a generic "Edit".
   */
  label?: string;
  /** Whether the cell can be edited (e.g. false when the row has no identity). */
  canEdit?: boolean;
  /** Commits a new value as `yyyy-MM-dd`. */
  onCommit: (value: string) => void;
  /**
   * Optional leading content rendered before the date (e.g. a status icon or a
   * marker element). Lets the caller decorate the cell without this component
   * knowing about domain concepts like "overridden".
   */
  adornment?: ReactNode;
}

/**
 * A date table cell that edits in place: display mode shows the formatted date
 * (with optional leading `adornment` and an edit pencil); clicking the pencil
 * swaps in the house DatePicker (calendar appended to `document.body` so it
 * isn't clipped by a scrolling table) and commits a `yyyy-MM-dd` value on
 * selection. Purely presentational — value, decoration, and commit handler are
 * supplied by the caller.
 *
 * @param {EditableDateCellProps} props - Value, label, optional adornment, and commit handler.
 * @return {JSX.Element} The editable date cell.
 */
export function EditableDateCell({
  value,
  label,
  canEdit = true,
  onCommit,
  adornment,
}: EditableDateCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [inputId] = useState(() => uniqueId('editable-date-'));
  const editLabel = label ? `Edit ${label}` : 'Edit';

  // Focusing the input opens the calendar (Carbon opens flatpickr on focus).
  useEffect(() => {
    if (isEditing) {
      document.getElementById(inputId)?.focus();
    }
  }, [isEditing, inputId]);

  if (isEditing) {
    return (
      <DatePicker
        id={inputId}
        labelText={label ?? ''}
        size="sm"
        value={value}
        appendTo={document.body}
        onChange={(dates) => {
          const picked = dates?.[0];
          if (picked && moment(picked).isValid()) {
            onCommit(moment(picked).format('YYYY-MM-DD'));
          }
        }}
        onClose={() => setIsEditing(false)}
      />
    );
  }

  return (
    <div className={styles.cell}>
      {adornment}
      <span className={styles.text}>
        {value ? (
          <Date date={value} format="MM/DD/YYYY" fallback={EMPTY} />
        ) : (
          EMPTY
        )}
      </span>
      {canEdit && (
        <EditPencilButton
          label={editLabel}
          onClick={() => setIsEditing(true)}
        />
      )}
    </div>
  );
}
